/**
 * Reusable file attach for conversation: document list + trigger button.
 * Supports .txt, .md, .csv, .json, .pdf, .docx (backend extracts text).
 */

import React, { useRef, useCallback } from "react";

import "./AttachFile.scss";

export const ALLOWED_FILE_ACCEPT =
  ".txt,.md,.csv,.json,.pdf,.docx,text/plain,text/markdown,text/csv,application/json,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document";

export const MAX_FILE_BYTES = 10 * 1024 * 1024; // 10 MB (matches backend)

export interface AttachedFile {
  contentBase64: string;
  mimeType: string;
  filename: string;
}

function readOneAsBase64(file: File): Promise<AttachedFile> {
  return new Promise((resolve, reject) => {
    if (file.size > MAX_FILE_BYTES) {
      reject(new Error(`File too large: ${file.name}`));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      const base64 = dataUrl.includes(",") ? dataUrl.split(",", 2)[1]! : "";
      resolve({
        contentBase64: base64,
        mimeType: file.type || "application/octet-stream",
        filename: file.name,
      });
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

export const readFilesAsBase64 = async (
  files: FileList | null,
  onReject?: (filenames: string[]) => void,
): Promise<AttachedFile[]> => {
  if (!files?.length) {
    return [];
  }
  const rejected: string[] = [];
  const allowed = Array.from(files).filter((f) => {
    const ext = f.name.split(".").pop()?.toLowerCase();
    const ok =
      ext &&
      [".txt", ".md", ".csv", ".json", ".pdf", ".docx"].includes(`.${ext}`);
    if (!ok) {
      return false;
    }
    if (f.size > MAX_FILE_BYTES) {
      rejected.push(f.name);
      return false;
    }
    return true;
  });
  const results: AttachedFile[] = [];
  for (const f of allowed) {
    try {
      results.push(await readOneAsBase64(f));
    } catch {
      // skip unreadable
    }
  }
  if (rejected.length && onReject) {
    onReject(rejected);
  }
  return results;
};

export interface AttachFileThumbnailsProps {
  files: AttachedFile[];
  onRemove: (index: number) => void;
  className?: string;
}

export const AttachFileThumbnails: React.FC<AttachFileThumbnailsProps> = ({
  files,
  onRemove,
  className = "",
}) => {
  if (!files.length) {
    return null;
  }
  return (
    <div className={`attach-file__list ${className}`.trim()}>
      {files.map((file, i) => (
        <div key={i} className="attach-file__item">
          <span className="attach-file__icon material-symbols-outlined">
            description
          </span>
          <span className="attach-file__name" title={file.filename}>
            {file.filename}
          </span>
          <button
            type="button"
            className="attach-file__remove"
            onClick={() => onRemove(i)}
            aria-label="Remove file"
          >
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>
      ))}
    </div>
  );
};

export interface AttachFileButtonProps {
  onAdd: (files: AttachedFile[]) => void;
  disabled?: boolean;
  buttonClassName?: string;
  title?: string;
}

export const AttachFileButton: React.FC<AttachFileButtonProps> = ({
  onAdd,
  disabled = false,
  buttonClassName = "",
  title = "Add file",
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleSelect = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const list = await readFilesAsBase64(e.target.files);
      if (list.length) {
        onAdd(list);
      }
      e.target.value = "";
    },
    [onAdd],
  );

  return (
    <>
      <button
        type="button"
        className={buttonClassName}
        onClick={() => fileInputRef.current?.click()}
        disabled={disabled}
        title={title}
        aria-label={title}
      >
        <span className="material-symbols-outlined">upload_file</span>
      </button>
      <input
        ref={fileInputRef}
        type="file"
        accept={ALLOWED_FILE_ACCEPT}
        multiple
        className="attach-file__file-input"
        onChange={handleSelect}
      />
    </>
  );
};
